import {
  AsyncPipe,
  DecimalPipe,
  NgTemplateOutlet,
  UpperCasePipe,
} from '@angular/common';
import { Component, computed, inject, input } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import type { OnyxScheduleCompliance } from '@onyx/angular';
import {
  ActionHelper,
  AddressHelper,
  OnyxAddressComponent,
  OnyxDatePipe,
  OnyxIconButtonComponent,
  OnyxIconComponent,
  OnyxLanguagePipe,
  OnyxLinkComponent,
  OnyxModalService,
  OnyxNamePipe,
  OnyxPhonePipe,
  OnyxTooltipComponent,
  OnyxTooltipDirective,
  UtilityHelper,
} from '@onyx/angular';
import { compact, differenceBy, isObject } from 'lodash';
import { map } from 'rxjs';
import { DelegationHelper } from '../../../../../dashboard/delegations/common/helpers/delegation.helper';
import type { SimplifiedDelegation } from '../../../../../dashboard/delegations/common/interfaces/delegation';
import { DriverHelper } from '../../../../../dashboard/drivers/common/helpers/driver.helper';
import type { SimplifiedDriver } from '../../../../../dashboard/drivers/common/interfaces/driver';
import { TechnicalPointCategory } from '../../../../../dashboard/fleet/common/enums/technical-point-category';
import { DelegationPointType } from '../../../../../dashboard/fleet/common/enums/technical-point-type';
import { FleetHelper } from '../../../../../dashboard/fleet/common/helpers/fleet.helper';
import type { SimplifiedFleet } from '../../../../../dashboard/fleet/common/interfaces/fleet';
import { ContractorHelper } from '../../../../../dashboard/management-panel/contractors/common/helpers/contractor.helper';
import type { SimplifiedContractor } from '../../../../../dashboard/management-panel/contractors/common/interfaces/contractor';
import { PointOfInterestHelper } from '../../../../../dashboard/management-panel/points-of-interest/common/helpers/point-of-interest.helper';
import { SwapModalHelper } from '../../../../../dashboard/map/common/helpers/swap/swap-modal.helper';
import type { DriverReportModalData } from '../../../../../dashboard/orders/common/components/driver-report-modal/driver-report-modal.component';
import { DriverReportModalComponent } from '../../../../../dashboard/orders/common/components/driver-report-modal/driver-report-modal.component';
import { OrderPointCategory } from '../../../../../dashboard/orders/common/enums/order-point-category';
import { OrderPointStatus } from '../../../../../dashboard/orders/common/enums/order-point-status';
import { OrderHelper } from '../../../../../dashboard/orders/common/helpers/order.helper';
import type { OrderLoadingGood } from '../../../../../dashboard/orders/common/interfaces/order-point';
import type { OrderPointDriverReports } from '../../../../../dashboard/orders/common/interfaces/order-point-driver-reports';
import { OrderFormService } from '../../../../../dashboard/orders/common/services/order-form.service';
import { RoutePointValueHelper } from '../../../../helpers/route-point-value.helper';
import { TransitHelper } from '../../../../helpers/transit.helper';
import type { TelematicsDriver } from '../../../../interfaces/telematics/telematics-driver';
import { GoodQuantityPipe } from '../../../../pipes/good-quantity.pipe';
import type { RoutePoint } from '../../../../services/route.service';
import { RouteService } from '../../../../services/route.service';
import type { RoutePointGoodsModalComponentData } from '../route-point-goods-modal/route-point-goods-modal.component';
import { RoutePointGoodsModalComponent } from '../route-point-goods-modal/route-point-goods-modal.component';
import { RouteHelper } from './../../../../helpers/route.helper';
import { FleetIdentifierPipe } from './../../../../pipes/fleet-identifier.pipe';
import { RoutePointDetailsStatusComponent } from './route-point-details-status/route-point-details-status.component';
import { ModuleLinkComponent } from '../../../module-link/module-link.component';

interface TransformedSwapData {
  label: string;
  callback: () => void;
}

@Component({
  selector: 'app-route-point-details',
  standalone: true,
  imports: [
    ModuleLinkComponent,
    OnyxIconComponent,
    OnyxTooltipComponent,
    TranslatePipe,
    OnyxAddressComponent,
    OnyxPhonePipe,
    UpperCasePipe,
    AsyncPipe,
    OnyxLanguagePipe,
    OnyxLinkComponent,
    FleetIdentifierPipe,
    DecimalPipe,
    OnyxTooltipDirective,
    FleetIdentifierPipe,
    GoodQuantityPipe,
    RoutePointDetailsStatusComponent,
    OnyxIconButtonComponent,
    OnyxNamePipe,
    OnyxDatePipe,
    NgTemplateOutlet,
  ],
  templateUrl: './route-point-details.component.html',
  styleUrl: './route-point-details.component.scss',
  host: {
    '[class]': `context`,
    '[class.tooltip-padding]': `isTooltip()`,
    '[class.preview]': `isPreview()`,
  },
})
export class RoutePointDetailsComponent {
  protected readonly actionHelper = inject(ActionHelper);
  protected readonly transitHelper = inject(TransitHelper);
  protected readonly pointOfInterestHelper = inject(PointOfInterestHelper);
  protected readonly modalService = inject(OnyxModalService);
  protected readonly routeService = inject(RouteService);
  protected readonly contractorHelper = inject(ContractorHelper);
  protected readonly orderHelper = inject(OrderHelper);
  private readonly driverHelper = inject(DriverHelper);
  protected readonly fleetHelper = inject(FleetHelper);
  private readonly delegationHelper = inject(DelegationHelper);
  private readonly namePipe = inject(OnyxNamePipe);
  private readonly fleetIdentifierPipe = inject(FleetIdentifierPipe);
  private readonly orderFormService = inject(OrderFormService, {
    optional: true,
  });
  private readonly swapModalHelper = inject(SwapModalHelper);

  public readonly point = input.required<RoutePoint>();
  public readonly goods = input.required<OrderLoadingGood[] | null>();
  public readonly compliance = input.required<OnyxScheduleCompliance | null>();
  public readonly typeIndex = input.required<number | null>();
  public readonly isTooltip = input(false);
  public readonly isPreview = input(false);

  protected readonly I18N = 'route.details';

  protected readonly AddressHelper = AddressHelper;
  protected readonly ActionHelper = ActionHelper;
  protected readonly isObject = isObject;
  protected readonly RouteHelper = RouteHelper;
  protected readonly TechnicalPointCategory = TechnicalPointCategory;
  protected readonly DelegationPointType = DelegationPointType;

  protected readonly context = this.routeService.context;

  protected isCompleted = computed(() => RouteHelper.isCompleted(this.point()));
  protected order = computed(() => {
    const point = this.point();
    return 'order' in point ? point.order : null;
  });
  protected category = computed(() => RouteHelper.getCategory(this.point()));
  protected swapData = computed(() => {
    const telematicsItem = this.routeService.telematicsItem();
    const swapData = RouteHelper.getSwapData(this.point());
    if (!swapData || !telematicsItem) return null;

    const item = this.routeService.getTelematicsItemByRoutePoint(
      telematicsItem,
      this.point(),
    );

    const added: TransformedSwapData[] = [];
    const removed: TransformedSwapData[] = [];

    const transform = (
      item: TelematicsDriver | SimplifiedDriver | SimplifiedFleet,
    ) => {
      const label =
        'driverData' in item
          ? this.namePipe.transform(item.driverData)
          : this.fleetIdentifierPipe.transform(item);

      const callback =
        'driverData' in item
          ? () => this.driverHelper.openModal(item.uuid)
          : () => this.fleetHelper.openModal(item.uuid);

      return {
        label,
        callback,
      };
    };

    const currentDrivers = compact(item.drivers);
    const drivers = compact([swapData.primaryDriver, swapData.secondaryDriver]);

    removed.push(
      ...differenceBy(currentDrivers, drivers, ({ uuid }) => uuid).map(
        transform,
      ),
    );
    added.push(
      ...differenceBy(drivers, currentDrivers, ({ uuid }) => uuid).map(
        transform,
      ),
    );

    const currentTrailer = item.assignedTrailer;
    if (currentTrailer?.uuid !== swapData.trailer?.uuid) {
      if (currentTrailer) removed.push(transform(currentTrailer));
      if (swapData.trailer) added.push(transform(swapData.trailer));
    }

    return {
      uuid: swapData.uuid,
      identifier: swapData.identifier,
      added: added.length ? added : null,
      removed: removed.length ? removed : null,
    };
  });
  protected realizationCrew = computed(() =>
    this.routeService.getRealizationCrew(this.point()),
  );
  protected delegationData = computed(() =>
    RouteHelper.getDelegationData(this.point()),
  );
  protected delegationTimeData = computed(() => {
    const data = this.delegationData();
    if (!data) return null;

    const delegation = data.delegation.delegation;
    const isStart = this.value().type === DelegationPointType.START;
    const target = isStart ? delegation.start : delegation.end;
    if (!target) return null;

    return {
      label: `labels.${isStart ? 'start' : 'end'}`,
      dateTime: `${target.date}T${target.time}`,
      plannedEnd: !isStart ? data.delegation.plannedEnd : null,
    };
  });
  protected includedInOrder = computed(() =>
    RouteHelper.isIncludedInOrder(this.value()),
  );
  protected address = computed(() => {
    const value = this.value();
    return 'address' in value ? value.address : value.startAddress;
  });
  protected addressName = computed(() => {
    const value = this.value();
    const address = this.address();

    if (address && 'poiName' in address && address.poiName) {
      return address.poiName;
    }
    const name = 'name' in value ? value.name : null;
    if (!name) return null;

    return RouteHelper.isDelegationPoint(value)
      ? `route.details.delegation.${name}`
      : name;
  });
  protected semiTrailer = computed(() => {
    const order = this.order() ?? this.routeService.order();
    const semiTrailer = order?.basicInformation?.loadedSemiTrailer;
    const uuid = RoutePointValueHelper.getValue(this.point()).uuid;
    if (!semiTrailer || !uuid) return null;

    const getResult = (key: 'pickup' | 'drop') => {
      const label = semiTrailer.data
        ? 'labels.semiTrailer'
        : 'labels.outsourcingSemiTrailer';

      return {
        data: semiTrailer.data ?? semiTrailer.registrationNumber,
        label: `${label}${key === 'pickup' ? 'Pickup' : 'Drop'}`,
      };
    };

    const { pickupPointUuid, dropPointUuid } = semiTrailer;
    if (uuid === pickupPointUuid) return getResult('pickup');
    if (uuid === dropPointUuid) return getResult('drop');

    return null;
  });
  protected phone = computed(() => {
    const value = this.value();
    return 'phone' in value ? value.phone : null;
  });
  protected referenceNumber = computed(() => {
    const value = this.value();
    return 'referenceNumber' in value ? value.referenceNumber : null;
  });
  protected hasDriverReport = computed(() => {
    const value = this.value();
    const driverReports = 'driverReports' in value ? value.driverReports : null;
    if (!driverReports) return false;

    return [
      OrderPointStatus.WAITING,
      OrderPointStatus.IN_PROGRESS,
      OrderPointStatus.COMPLETED,
    ].some(
      (status) =>
        driverReports[status as keyof OrderPointDriverReports] != null,
    );
  });

  protected transitRoute$ = computed(() => {
    const value = this.value();
    if (value.category !== OrderPointCategory.TRANSIT) return null;

    const transitData$ = UtilityHelper.maybeAsyncToObservable(
      this.transitHelper.getRoutesSource(value.type).get(value.name ?? ''),
    );
    return transitData$.pipe(map((data) => data?.value ?? null));
  });

  protected isHandlingPoint = computed(() =>
    RouteHelper.isHandlingPoint(this.point()),
  );
  protected poiLink = computed(() => {
    const address = this.address();
    const poiUuid = address && 'poiUuid' in address ? address.poiUuid : null;
    if (!poiUuid) return null;

    return () => this.pointOfInterestHelper.openModal(poiUuid);
  });

  protected value = computed(() =>
    RoutePointValueHelper.getValue(this.point()),
  );

  protected openSenderModal(sender: SimplifiedContractor): void {
    this.contractorHelper.openModal(sender);
  }

  protected openOrderModal(uuid: string): void {
    this.orderHelper.openModal(uuid);
  }

  protected openDriverReportModal(): void {
    const point = this.point();
    const value = this.value();
    const order = 'order' in point ? point.order : this.routeService.order();
    const pointValue = 'status' in value ? value : null;
    if (!pointValue) return;

    this.modalService.open<DriverReportModalData>(DriverReportModalComponent, {
      point: pointValue,
      typeIndex: this.typeIndex(),
      orderIdentifier: order?.identifier ?? null,
      orderUuid: order?.uuid ?? null,
    });
  }

  protected openSwapModal(): void {
    const swapData = this.swapData();
    if (!swapData) return;

    const vehicleUuid = this.routeService.vehicle()?.uuid ?? null;
    this.swapModalHelper.openModal(swapData.uuid, vehicleUuid);
  }

  protected openFleetModal(uuid: string): void {
    this.fleetHelper.openModal(uuid);
  }

  protected openDriverModal(driver: SimplifiedDriver | string): void {
    this.driverHelper.openModal(driver);
  }

  protected openDelegationModal(
    simplifiedDelegation: SimplifiedDelegation,
  ): void {
    this.delegationHelper.openModal(simplifiedDelegation);
  }

  protected openGoodsPreview(): void {
    const goods = this.goods();
    if (!goods) return;

    this.modalService.open<RoutePointGoodsModalComponentData>(
      RoutePointGoodsModalComponent,
      {
        point: this.value() as RoutePointGoodsModalComponentData['point'],
        goods,
      },
      {
        providers: [
          { provide: OrderFormService, useValue: this.orderFormService },
        ],
      },
    );
  }
}
